import ArticleCard from "@elements/Card/Article/ArticleCard";
import Heading from "@elements/Typography/Heading";
import { isArrayWithElements } from "@helper/utils";
import { ProcessedArticleData } from "@hooks/transformer/useOSResponse";
import { ArticleCardVariant } from "@src/app/config/articleCard";
import { MoreLinkProps } from "@typings/StoryPackage.d";
import { twMerge } from "tailwind-merge";

import MoreLink from "./MoreLink";

export type VerticalStoryPackageStyles = {
  wrapperStyle?: string;
  titleStyle?: string;
  moreLinkStyle?: string;
};

export type ArticleCardProps = {
  hasParagraph?: boolean;
  hasSectionName?: boolean;
  hasNewIcon?: boolean;
};

export type TitleIconImgProps = {
  src: string;
  width: number;
  height: number;
};

export type VerticalStoryPackageProps = {
  firstCardVariant: ArticleCardVariant;
  restCardVariant: ArticleCardVariant;
  renderItems: ProcessedArticleData[];
  title?: string;
  styles?: VerticalStoryPackageStyles;
  moreLink?: MoreLinkProps;
  titleIcon?: TitleIconImgProps;
};

export default function VerticalStoryPackage({
  firstCardVariant = "featured",
  restCardVariant = "2md",
  title,
  renderItems,
  styles = {
    wrapperStyle: "",
    titleStyle: "",
    moreLinkStyle: "",
  },
  moreLink,
  titleIcon,
}: VerticalStoryPackageProps) {
  const { wrapperStyle, titleStyle, moreLinkStyle } = styles;
  const firstArticle = isArrayWithElements(renderItems) && renderItems[0];

  return (
    <div
      className={twMerge(
        "my-sm py-xs border border-red-600 border-x-0 border-b-0 border-t-2",
        wrapperStyle,
      )}
    >
      {title && (
        <div
          className={twMerge(
            "text-sm font-semibold text-grey-350 leading-[24px] mb-xs",
            titleStyle,
          )}
        >
          {titleIcon && (
            <img
              src={titleIcon.src}
              alt="title-icon"
              width={titleIcon.width}
              height={titleIcon.height}
            />
          )}
          <Heading Element="h4" className="font-primary" testId="heading">
            {title}
          </Heading>
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-md">
        <div className="basis-3/6 lg:basis-4/6">
          {firstArticle && (
            <ArticleCard
              key={firstArticle.id}
              {...firstArticle}
              orientation="horizontal"
              variant={firstCardVariant}
              testId="article-0"
              forNewDesign={true}
              displayWidth={488}
              displayHeight={366}
            />
          )}
        </div>
        <div className="basis-3/6 lg:basis-2/6 flex flex-col gap-xs">
          {renderItems.slice(1, 4).map((item, idx) => {
            return (
              <ArticleCard
                key={item.id}
                {...item}
                orientation="horizontal"
                variant={restCardVariant}
                testId={`article-${idx + 1}`}
                forNewDesign={true}
                displayWidth={113}
                displayHeight={86}
              />
            );
          })}
        </div>
      </div>
      {moreLink && (
        <div
          className={twMerge(
            "flex items-center justify-end text-xxs leading-[18px] text-grey-350 font-semibold font-primary border-white-600 border-t-[1px] mt-xs pt-xs",
            moreLinkStyle,
          )}
        >
          <MoreLink
            {...moreLink}
            className={twMerge("flex gap-1", moreLink.className)}
            testId="more-link"
          />
        </div>
      )}
    </div>
  );
}
